class CartProgressBar extends HTMLElement {
  constructor() {
    super();
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this));
  }

  connectedCallback() {
    this.fill = this.querySelector('[data-progress-fill]');
    this.textEl = this.querySelector('[data-progress-message]');
    this.milestones = this.querySelectorAll('[data-milestone]');

    this.m1 = parseInt(this.dataset.m1Threshold, 10);
    this.m2 = parseInt(this.dataset.m2Threshold, 10);
    this.m1Label = this.dataset.m1Label;
    this.m2Label = this.dataset.m2Label;
    this.m1Pos = parseFloat(this.dataset.m1Pos);
    this.initialProgress = Number(this.dataset.initialProgress);
    this.currentProgressState = this.getProgressState(parseInt(this.dataset.cartTotal, 10) || 0);

    const cartTotal = parseInt(this.dataset.cartTotal, 10) || 0;
    this.style.display = cartTotal > 0 ? 'block' : 'none';

    if (!isNaN(this.initialProgress) && this.fill) {
      const clamped = Math.min(100, Math.max(0, this.initialProgress));
      this.fill.style.setProperty('--progress', `${clamped.toFixed(2)}%`);

      this.milestones.forEach((milestone) => {
        const target = parseFloat(milestone.dataset.progress) || 0;
        milestone.classList.toggle('is-reached', clamped >= target);
      });
    }
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
  }

  async onCartUpdate({ cartData }) {
    let price = cartData?.total_price;

    if (price == null) {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      price = cart.total_price;
    }

    price = parseInt(price) || 0;
    const progress = this.calculateProgress(price);

    this.fill.style.setProperty('--progress', `${progress.toFixed(2)}%`);

    this.milestones.forEach((milestone) => {
      const target = parseFloat(milestone.dataset.progress) || 0;
      milestone.classList.toggle('is-reached', progress >= target);
    });

    this.style.display = price > 0 ? 'block' : 'none';
    await this.updateText(price);
  }

  calculateProgress(cartTotal) {
    let pct = 0;

    if (cartTotal >= this.m2) {
      pct = 100;
    } else if (cartTotal >= this.m1) {
      const range = this.m2 - this.m1;
      const pastM1 = cartTotal - this.m1;
      const segmentPct = (pastM1 / range) * 100;
      const segmentWidth = 100 - this.m1Pos;
      pct = this.m1Pos + (segmentPct * segmentWidth) / 100;
    } else if (cartTotal > 0) {
      pct = (cartTotal * this.m1Pos) / this.m1;
    }

    return Math.min(100, Math.max(0, pct));
  }

  getProgressState(price) {
    if (price >= this.m2) return 'm2_reached';
    if (price >= this.m1) return 'm1_reached';
    if (price > 0) return 'm1_remaining';
    return 'none';
  }

  getMessage(state, price) {
    const strings = window.cartStrings || {};
    const m1Remaining = Math.max(this.m1 - price, 0);
    const m2Remaining = Math.max(this.m2 - price, 0);
    const moneyFormat = window.theme?.settings?.money_format || '{{amount}}';
    const fmt = (cents) => formatPrice(cents, moneyFormat);

    switch (state) {
      case 'm2_reached':
        return `<p class="caption-l">${(strings.progressM2Reached || '').replace('[reward]', this.m2Label)}</p>`;
      case 'm1_reached':
        return `<p class="caption-l">${(strings.progressM1Reached || '').replace('[reward]', this.m1Label)}</p>`
          + `<p class="caption-m">${(strings.progressM2Remaining || '').replace('[amount]', fmt(m2Remaining)).replace('[reward]', this.m2Label)}</p>`;
      case 'm1_remaining':
        return `<p class="caption-l">${(strings.progressM1Remaining || '').replace('[amount]', fmt(m1Remaining)).replace('[reward]', this.m1Label)}</p>`;
      default:
        return '';
    }
  }

  async updateText(price) {
    if (!this.textEl) return;

    const newState = this.getProgressState(price);
    const message = this.getMessage(newState, price);

    if (newState === this.currentProgressState) {
      this.textEl.innerHTML = message;
      return;
    }

    this.currentProgressState = newState;

    await this.waitForClassTransition('changing');
    this.textEl.innerHTML = message;
    this.textEl.classList.remove('changing');

    await this.waitForClassTransition('changed');
    this.textEl.classList.remove('changed');
  }

  waitForClassTransition(className) {
    if (!this.textEl) return Promise.resolve();
    this.textEl.classList.add(className);

    return Promise.race([
      new Promise((resolve) => {
        const handler = (event) => {
          if (event.target === event.currentTarget) {
            this.textEl.removeEventListener('transitionend', handler);
            resolve();
          }
        };
        this.textEl.addEventListener('transitionend', handler);
      }),
      new Promise((resolve) => setTimeout(resolve, 350)),
    ]);
  }
}

customElements.define('cart-progress-bar', CartProgressBar);
